<template>
    <div class="border-t border-gray-200">
        <!-- Collapsible toggle (chat): compact, collapsed by default so it does
             not crowd the composer. -->
        <button
            v-if="collapsible"
            type="button"
            class="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-gray-400 transition hover:text-gray-600"
            @click="expanded = !expanded"
        >
            <TagIcon class="h-3.5 w-3.5 shrink-0" />
            <span>{{ t("vue.ai_tags_label") }}</span>
            <span
                v-if="names.length > 0"
                class="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold normal-case text-gray-500"
            >
                {{ names.length }}
            </span>
            <ChevronDownIcon
                class="ml-auto h-4 w-4 transition-transform"
                :class="expanded ? 'rotate-180' : ''"
            />
        </button>

        <p
            v-else
            class="px-4 pb-2 pt-4 text-[11px] font-medium uppercase tracking-wide text-gray-400"
        >
            {{ t("vue.ai_tags_label") }}
        </p>

        <div
            v-show="!collapsible || expanded"
            class="flex flex-wrap items-center gap-1.5 px-4 pb-4"
        >
            <AiTagChip
                v-for="name in names"
                :key="name"
                :label="name"
                removable
                :remove-label="t('vue.ai_tags_remove')"
                @remove="removeTag(name)"
            />

            <form
                class="inline-flex items-center gap-1"
                @submit.prevent="addFromInput"
            >
                <input
                    v-model="draft"
                    :list="datalistId"
                    :placeholder="t('vue.ai_tags_add')"
                    class="h-7 w-40 rounded-full border border-gray-300 px-3 text-xs focus:border-gray-500 focus:outline-none"
                    maxlength="50"
                />
                <datalist :id="datalistId">
                    <option
                        v-for="option in availableTags"
                        :key="option.slug"
                        :value="option.name"
                    />
                </datalist>
                <button
                    type="submit"
                    class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-[#FF421E] transition hover:bg-[#FF421E]/10 disabled:opacity-40"
                    :disabled="draft.trim() === ''"
                    :aria-label="t('vue.ai_tags_add')"
                >
                    <PlusIcon class="h-4 w-4" />
                </button>
            </form>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import { PlusIcon, TagIcon, ChevronDownIcon } from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type { IAiTag } from "@/types/AiChat/IAiTag";

const props = withDefaults(
    defineProps<{
        // Unique key for the tagged entity (resets local state + datalist id).
        entityKey: number | string;
        submitUrl: string;
        tags: IAiTag[];
        availableTags: IAiTag[];
        collapsible?: boolean;
    }>(),
    { collapsible: false },
);

const { t } = useTranslations();

const names = ref<string[]>(props.tags.map((tag) => tag.name));
const draft = ref("");
const expanded = ref(false);

const datalistId = computed<string>(() => `ai-tag-options-${props.entityKey}`);

// Reset local chips when a different entity is selected.
watch(
    () => props.entityKey,
    () => {
        names.value = props.tags.map((tag) => tag.name);
        draft.value = "";
        expanded.value = false;
    },
);

const addFromInput = (): void => {
    const value = draft.value.trim();
    if (value === "") {
        return;
    }
    const exists = names.value.some(
        (name) => name.toLowerCase() === value.toLowerCase(),
    );
    if (!exists) {
        names.value = [...names.value, value];
        submit();
    }
    draft.value = "";
};

const removeTag = (name: string): void => {
    names.value = names.value.filter((current) => current !== name);
    submit();
};

const submit = (): void => {
    router.post(
        props.submitUrl,
        { tags: names.value },
        {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () =>
                router.reload({
                    only: ["chats", "activeChat", "items", "selected"],
                }),
        },
    );
};
</script>
